import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpParamNames } from 'src/app/core/constantes/http_param-names';
import { BoxGroup } from 'src/app/models/server/box-group';
import { EnumModeResetPassword } from 'src/app/models/server/enums/enum-mode-reset-password';
import { EnumReasonResetPassword } from 'src/app/models/server/enums/enum-reason-reset-password';
import { ListLayout } from 'src/app/models/server/list-layout';
import { View } from 'src/app/models/server/view';
import { PropertyType } from '../../models/enums/property-type';
import { ColumnDefinition } from '../../models/server/column-definition';
import { Context } from '../../models/server/core/context';
import { FilterInfos } from '../../models/server/core/filter-infos';
import { Results } from '../../models/server/core/results-T';
import { SortInfos } from '../../models/server/core/sort-infos';
import { User } from '../../models/server/user';
import { WorkflowStepColumnDefinition } from '../../models/server/workflow-step-column-definition';
import { AdvancedService } from '../core/advanced.service';
import { ConnectedUsersStateService } from '../state/connected-users-state.service';
import { ServiceBase } from './service-base';

@Injectable({
    providedIn: 'root',
})
export class UsersService extends ServiceBase implements AdvancedService<User> {
    /**
     * Constructeur
     */
    constructor(
        private http: HttpClient,
        public override connectedUserStateService: ConnectedUsersStateService
    ) {
        super(connectedUserStateService);
    }

    List(context: Context, filters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<User>> {
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                sort: this.getHeader(sortInfos),
                filters: this.getHeader(filters),
                context: this.getHeader(context),
            }),
            params: new HttpParams().set(HttpParamNames.FROM, pageNumber.toString()).set(HttpParamNames.SIZE, pageSize.toString()),
        };

        return this.http.get(this.baseUrl + `/api/organizations/${this.organizationId}/users`, httpOptions).pipe(
            map((x) => {
                var res = Results.getCopyFrom<User>(User, x);
                return res;
            })
        );
    }

    /**
     * Récupère les informations d'un utilisateur
     * @param userId Identifiant de l'utilisateur
     * @returns Retourne un utilisateur
     */
    get(userId: string): Observable<User> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<User>(this.baseUrl + `/api/organizations/${this.userConnected.organization.id}/users/${userId}`, httpOptions).pipe(map((x) => User.createFormCopy(x)));
    }

    /**
     * Récupère les bannettes d'une vue sélectionné par défaut pour l'utilisateur courant.
     * @param viewId Identifiant de la vue.</param>
     * @returns Une liste d'id de bannettes</returns>
     */
    public GetCurrentUserDefaultBoxSelection(viewId: number): Observable<string[]> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<string[]>(this.baseUrl + `/api/organizations/${this.userConnected.organization.id}/views/${viewId}/boxes/defaultselection`, httpOptions);
    }

    /**
     * Récupère la liste des groupes de bannettes d'une vue accessibles par l'utilisateur courant.
     * @param : Id de la vue.
     * @returns la liste des groupes de bannettes.
     */
    public getViewBoxGroups(viewId: number): Observable<BoxGroup[]> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<BoxGroup[]>(
            this.baseUrl + `/api/organizations/${this.userConnected.organization.id}/views/${viewId}/users/${this.userConnected.userId}/boxgroups`,
            httpOptions
        );
    }

    /**
     * Récupère la liste des colonnes a afficher pour la vue
     * @returns la liste des colonnes
     */
    public getViewColumnsDefinitions(viewId: number): Observable<ColumnDefinition[]> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };
        return this.http
            .get<
                ColumnDefinition[]
            >(this.baseUrl + `/api/organizations/${this.userConnected.organization.id}/views/${viewId}/users/${this.userConnected.userId}/columndefinitions`, httpOptions)
            .pipe(
                map((definitions) =>
                    definitions.map((definition) => {
                        if (definition.type === PropertyType.Workflow) {
                            return WorkflowStepColumnDefinition.from(definition);
                        } else {
                            return ColumnDefinition.from(definition);
                        }
                    })
                )
            );
    }

    /**
     * Récupère la mise en page d'une vue utilisée par l'utilisateur.
     * @param viewId Id de la vue.
     * @returns la mise en page.
     */
    public getViewListLayout(viewId: number): Observable<ListLayout> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<ListLayout>(
            this.baseUrl + `/api/organizations/${this.userConnected.organization.id}/views/${viewId}/users/${this.userConnected.userId}/listLayout`,
            httpOptions
        );
    }

    /**
     * Récupère la disposition des colonnes de type bannette utilisée par l'utilisateur
     * @returns la disposition des colonnes
     */
    public getViews(): Observable<View[]> {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
        };

        return this.http.get<View[]>(this.baseUrl + `/api/organizations/${this.userConnected.organization.id}/users/${this.userConnected.userId}/views`, httpOptions);
    }

    StartPasswordReset(userId: string, reason: EnumReasonResetPassword, mode: EnumModeResetPassword, inputUrl: string, timeout: number = null): Observable<string | null> {
        let httpOptions = {
            headers: new HttpHeaders().set('Content-Type', 'application/json'),
            params: new HttpParams().set('reason', reason).set('mode', mode).set('inputUrl', inputUrl),
        };

        if (timeout) {
            httpOptions.params = httpOptions.params.append('timeout', timeout);
        }

        return this.http.post<string>(`${this.baseUrl}/api/users/${userId}/resetPassword`, null, httpOptions);
    }
}
